import Axios from 'axios';
import * as R from 'ramda';
import {
  API_MODE,
  API_URL,
  LEGACY,
} from './config';

const axiosInstance = Axios.create({
  baseURL: API_URL,
});

if (API_MODE === LEGACY) {
  // Define a list of regex patterns for our different API endpoints.
  const REQ_CATEGORIES_COLLECTION_PATTERN = /\/definition\/categories$/g;
  const REQ_CATEGORIES_PATTERN = /\/definition\/categories\/.+$/g;
  const REQ_QUERIES_PATTERN = /\/analytics\/vbql$/g;

  // Since we rewrite the URL in request, we have to match the rewritten URL.
  const RESP_CATEGORIES_COLLECTION_PATTERN = /\/analytics\/categories$/g;
  const RESP_CATEGORIES_PATTERN = /\/analytics\/categories\/.+$/g;
  const RESP_QUERIES_PATTERN = /\/analytics\/query$/g;

  // Transform v3 category format to legacy
  const rewriteRequestCategory = category => ({
    ...R.omit(['query'], category),
    fullQuery: category.query,
    tags: category.tags && category.tags.length ? category.tags.join(',') : '',
  });

  // Transform legacy category format to v3
  const rewriteResponseCategory = (category) => ({
    ...R.omit(['fullQuery'], category),
    query: category.fullQuery,
    tags: category.tags && category.tags.length ? category.tags.split(',') : [],
  });

  // Helper function to detect whether this request is for the right resource and method(s)
  const matchAPIRequest = (config, urlPattern, ...methods) => (
    config.url.match(urlPattern)
    && methods.map(m => m.toLowerCase()).includes(config.method.toLowerCase())
  );

  // Requests require us to rewrite the endpoint url to its legacy equivalent.
  // Requests that send data to the server need to also transform the request body.
  axiosInstance.interceptors.request.use(
    (config) => {
      // GET /definition/categories
      if (matchAPIRequest(config, REQ_CATEGORIES_COLLECTION_PATTERN, 'GET')) {
        return {
          ...config,
          url: config.url.replace('/definition/categories', '/analytics/categories'),
        };
      }

      // GET, DELETE /definition/categories/:id
      if (matchAPIRequest(config, REQ_CATEGORIES_PATTERN, 'GET', 'DELETE')) {
        return {
          ...config,
          url: config.url.replace('/definition/categories', '/analytics/categories'),
        };
      }

      // PUT /definition/categories/:id
      if (matchAPIRequest(config, REQ_CATEGORIES_PATTERN, 'PUT')) {
        return {
          ...config,
          url: config.url.replace('/definition/categories', '/analytics/categories'),
          data: rewriteRequestCategory(config.data),
        };
      }

      // POST /analytics/vbql
      if (matchAPIRequest(config, REQ_QUERIES_PATTERN, 'POST')) {
        return {
          ...config,
          url: config.url.replace('/analytics/vbql', '/analytics/query'),
        };
      }

      return config;
    },
    (error) => {
      console.log(error);
      // Do something with response error
      return Promise.reject(error);
    },
  );

  // Responses require us to rewrite the endpoint url -BACK TO- its v3 equivalent.
  // Responses that receive data from the server need to also transform the response body.
  axiosInstance.interceptors.response.use(
    (response) => {
      // GET /definition/categories
      if (matchAPIRequest(response.config, RESP_CATEGORIES_COLLECTION_PATTERN, 'GET')) {
        return {
          ...response,
          config: {
            ...response.config,
            url: response.config.url.replace('/definition/categories', '/analytics/categories'),
          },
          data: {
            categories: response.data.map(rewriteResponseCategory),
          },
        };
      }

      // GET, PUT /definition/categories/:id
      if (matchAPIRequest(response.config, RESP_CATEGORIES_PATTERN, 'GET', 'PUT')) {
        return {
          ...response,
          config: {
            ...response.config,
            url: response.config.url.replace('/definition/categories', '/analytics/categories'),
          },
          data: rewriteResponseCategory(response.data),
        };
      }

      // DELETE /definition/categories/:id
      if (matchAPIRequest(response.config, RESP_CATEGORIES_PATTERN, 'DELETE')) {
        return {
          ...response,
          config: {
            ...response.config,
            url: response.config.url.replace('/definition/categories', '/analytics/categories'),
          },
        };
      }

      // POST /analytics/vbql
      if (matchAPIRequest(response.config, RESP_QUERIES_PATTERN, 'POST')) {
        return {
          ...response,
          config: {
            ...response.config,
            url: response.config.url.replace('/analytics/query', '/analytics/vbql'),
          },
        };
      }
      // Do something with response data
      return response;
    },
    (error) => {
      console.log(error);
      // Do something with response error
      return Promise.reject(error);
    },
  );
}

export default axiosInstance;
